import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const QuoteRequest = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center" data-testid="quote-success">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Quote Request Submitted!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for your interest. Our team will review your request and get back to you within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/products')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="browse-products-btn"
              >
                Browse Products
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Request a Quote</h1>
          <p className="text-lg text-gray-600">Tell us about your needs and we'll provide a custom quote</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  placeholder="John Doe"
                  data-testid="quote-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  placeholder="john@company.com"
                  data-testid="quote-email-input"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  placeholder="ABC Restaurant"
                  data-testid="quote-company-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                  placeholder="(555) 123-4567"
                  data-testid="quote-phone-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Message / Requirements *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none resize-none"
                placeholder="Please tell us about your requirements: product quantities, delivery timeline, special requests, etc."
                data-testid="quote-message-input"
              />
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Need help choosing the right products?</strong><br />
                Our team can recommend the best cup sizes and quantities for your business type. 
                We'll also provide information about tiered pricing discounts.
              </p>
            </div>

            <Button 
              type="submit"
              disabled={submitting}
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
              data-testid="submit-quote-btn"
            >
              <Send className="mr-2 h-5 w-5" />
              {submitting ? 'Submitting...' : 'Submit Quote Request'}
            </Button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Prefer to Talk Directly?</h3>
          <p className="text-gray-600 mb-4">Our sales team is ready to help you</p>
          <div className="space-y-2 text-gray-700">
            <p><strong>Phone:</strong> <a href="tel:732-997-0792" className="text-blue-600 hover:underline">732-997-0792</a></p>
            <p><strong>Email:</strong> <a href="mailto:info@dcpac.co" className="text-blue-600 hover:underline">info@dcpac.co</a></p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default QuoteRequest;